import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { get, onValue, push, ref, update } from 'firebase/database';
import { Loader2, Mail, Pencil, Phone, Plus } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import ActionModal from '../components/ActionModal';
import { auth, database } from '../services/firebase';

type UserProfile = {
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
};

type ClinicRecord = {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  description?: string | null;
  status: 'active' | 'paused' | 'closed';
  phone?: string;
  email?: string;
  createdAt?: number;
  createdBy?: string;
  organizationId?: string;
  updatedAt?: number;
  type?: string | null;
};

type ClinicFormState = {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  description: string;
  phone: string;
  email: string;
  status: ClinicRecord['status'];
  type: string;
};

const clinicStatusOptions: Array<{ value: ClinicRecord['status']; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
];

const locationTypeOptions = [
  'Veterinary clinic',
  'Veterinary hospital',
  'Boarding',
  'Groomer',
  'Training / Behavior',
  'Rescue / Shelter',
  'Mobile services',
  'Pharmacy',
  'Pet store / Boutique',
  'Dog walker',
  'Pet daycare',
  'Other',
];

const initialClinicFormState: ClinicFormState = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  description: '',
  phone: '',
  email: '',
  status: 'active',
  type: '',
};

// Put your key in Vite as VITE_GOOGLE_MAPS_KEY (or swap to whatever you use)
const GOOGLE_MAPS_KEY = 'AIzaSyDEGMqyA5NtiozNRFBimsc5q1rZOs0KTaU';

function clinicAddressLine(c: ClinicRecord) {
  return [c.address, c.city, c.state, c.zip].filter(Boolean).join(', ');
}

function staticMapUrl(c: ClinicRecord, width = 640, height = 400, zoom = 15) {
  const addr = clinicAddressLine(c);
  const base = 'https://maps.googleapis.com/maps/api/staticmap';
  const params = new URLSearchParams({
    center: addr,
    zoom: String(zoom),
    size: `${width}x${height}`,
    scale: '2', // retina crisp
    markers: `color:blue|${addr}`,
    key: GOOGLE_MAPS_KEY ?? '',
  });
  return `${base}?${params.toString()}`;
}

export default function Locations() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [clinics, setClinics] = useState<ClinicRecord[]>([]);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [clinicsError, setClinicsError] = useState<string | null>(null);
  const [clinicModalOpen, setClinicModalOpen] = useState(false);
  const [editingClinicId, setEditingClinicId] = useState<string | null>(null);
  const [clinicForm, setClinicForm] = useState<ClinicFormState>(initialClinicFormState);
  const [submittingClinic, setSubmittingClinic] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const clinicNameInputRef = useRef<HTMLInputElement | null>(null);
  const [organizationHasSubscription, setOrganizationHasSubscription] = useState<boolean | null>(null);
  const [organizationDefaultType, setOrganizationDefaultType] = useState<string | null>(null);

  const groupedClinics = useMemo(() => {
    const groups: Record<ClinicRecord['status'], ClinicRecord[]> = {
      active: [],
      paused: [],
      closed: [],
    };
    for (const c of clinics) {
      groups[c.status ?? 'active'].push(c);
    }
    return groups;
  }, [clinics]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/locations' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    const profileRef = ref(database, `users/${currentUser.uid}`);
    const unsubscribeProfile = onValue(
      profileRef,
      snapshot => {
        const value = snapshot.val() as UserProfile | null;
        if (value?.needsOrganizationSetup) {
          setProfileLoading(false);
          navigate('/organization/create', {
            replace: true,
            state: { returnTo: '/locations' },
          });
          return;
        }
        setProfile(value);
        setProfileLoading(false);
      },
      error => {
        console.warn('Failed to load users profile data', error);
        setProfileLoading(false);
      },
    );

    return () => unsubscribeProfile();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!profile?.organizationId) {
      setClinics([]);
      setClinicsError(!profileLoading ? (profile?.needsOrganizationSetup ? null : 'No organization details were found.') : null);
      setLoadingClinics(false);
      setOrganizationDefaultType(null);
      return;
    }

    const organizationId = profile.organizationId;
    const joinRef = ref(database, `organizationLocations/${organizationId}`);
    void get(ref(database, `organizations/${organizationId}`))
      .then(snapshot => {
        const value = snapshot.val() as { subscriptionId?: unknown; type?: unknown } | null;
        const hasSubscription = typeof value?.subscriptionId === 'string' ? value.subscriptionId.length > 0 : false;
        setOrganizationHasSubscription(hasSubscription);
        setOrganizationDefaultType(typeof value?.type === 'string' ? value.type : null);
      })
      .catch(error => {
        console.warn('Failed to load organization details for locations view', error);
        setOrganizationHasSubscription(null);
        setOrganizationDefaultType(null);
      });
    let isActive = true;
    let clinicOrder: string[] = [];
    let pendingClinicIds = new Set<string>();
    let attemptedLegacyMigration = false;

    const clinicUnsubscribers = new Map<string, () => void>();
    const clinicDataMap = new Map<string, ClinicRecord>();

    const cleanupClinicSubscriptions = () => {
      clinicUnsubscribers.forEach(unsubscribeClinic => {
        unsubscribeClinic();
      });
      clinicUnsubscribers.clear();
      clinicDataMap.clear();
    };

    const updateClinicsState = () => {
      if (!isActive) {
        return;
      }
      const clinicsList = clinicOrder
        .map(clinicId => clinicDataMap.get(clinicId))
        .filter((clinic): clinic is ClinicRecord => Boolean(clinic));
      setClinics(clinicsList);
    };

    const subscribeToClinic = (clinicId: string) => {
      const clinicRef = ref(database, `locations/${clinicId}`);

      const unsubscribeClinic = onValue(
        clinicRef,
        snapshot => {
          if (!isActive) {
            return;
          }

          const value = snapshot.val() as (Partial<ClinicRecord> & { type?: string; organizationType?: string }) | null;
          if (!value) {
            clinicDataMap.delete(clinicId);
          } else {
            const normalizedStatus =
              typeof value.status === 'string'
                ? (value.status.toLowerCase() as ClinicRecord['status'])
                : 'active';
            const clinicType =
              typeof value.type === 'string'
                ? value.type
                : typeof value.organizationType === 'string'
                  ? value.organizationType
                  : null;
            const nextClinic: ClinicRecord = {
              id: clinicId,
              name: value.name ?? '',
              address: value.address ?? '',
              city: value.city ?? '',
              state: value.state ?? '',
              zip: value.zip ?? '',
              description: typeof value.description === 'string' ? value.description : null,
              status: normalizedStatus,
              createdAt: value.createdAt,
              createdBy: value.createdBy,
              organizationId: typeof value.organizationId === 'string' ? value.organizationId : organizationId,
              type: clinicType,
            };
            if (typeof value.phone === 'string') {
              nextClinic.phone = value.phone;
            }
            if (typeof value.email === 'string') {
              nextClinic.email = value.email;
            }
            clinicDataMap.set(clinicId, nextClinic);
          }

          pendingClinicIds.delete(clinicId);
          setClinicsError(null);
          updateClinicsState();
          if (pendingClinicIds.size === 0) {
            setLoadingClinics(false);
          }
        },
        error => {
          if (!isActive) {
            return;
          }
          console.warn(`Failed to subscribe to clinic ${clinicId}`, error);
          clinicDataMap.delete(clinicId);
          pendingClinicIds.delete(clinicId);
          setClinicsError('We were unable to load clinics. Please try again later.');
          updateClinicsState();
          if (pendingClinicIds.size === 0) {
            setLoadingClinics(false);
          }
        },
      );

      clinicUnsubscribers.set(clinicId, unsubscribeClinic);
    };

    setLoadingClinics(true);
    setClinicsError(null);

    const unsubscribeJoin = onValue(
      joinRef,
      snapshot => {
        if (!isActive) {
          return;
        }

        const value = snapshot.val() as Record<string, { addedAt?: number } | null> | null;
        const nextIds = value ? Object.keys(value) : [];
        clinicOrder = [...nextIds].sort((a, b) => {
          const aAdded = value?.[a]?.addedAt ?? 0;
          const bAdded = value?.[b]?.addedAt ?? 0;
          return aAdded - bAdded;
        });

        const nextIdSet = new Set(nextIds);
        clinicUnsubscribers.forEach((unsubscribeClinic, clinicId) => {
          if (!nextIdSet.has(clinicId)) {
            unsubscribeClinic();
            clinicUnsubscribers.delete(clinicId);
            clinicDataMap.delete(clinicId);
          }
        });

        if (nextIds.length === 0) {
          let keepLoading = false;
          if (!attemptedLegacyMigration) {
            attemptedLegacyMigration = true;
            keepLoading = true;
            void (async () => {
              try {
                const legacySnapshot = await get(ref(database, `organizations/${organizationId}/locations`));
                const legacyValue = legacySnapshot.val() as Record<string, Partial<ClinicRecord>> | null;
                if (legacyValue) {
                  const updates: Record<string, unknown> = {};
                  const migrationTimestamp = Date.now();

                  for (const [legacyClinicId, legacyClinicData] of Object.entries(legacyValue)) {
                    if (!legacyClinicData || typeof legacyClinicData !== 'object') {
                      continue;
                    }
                    const normalizedStatus =
                      typeof legacyClinicData.status === 'string'
                        ? (legacyClinicData.status.toLowerCase() as ClinicRecord['status'])
                        : 'active';
                    const migratedClinicData: Record<string, unknown> = {
                      id: legacyClinicId,
                      organizationId,
                      name: legacyClinicData.name ?? '',
                      address: legacyClinicData.address ?? '',
                      city: legacyClinicData.city ?? '',
                      state: legacyClinicData.state ?? '',
                      zip: legacyClinicData.zip ?? '',
                      description: legacyClinicData.description ?? null,
                      status: normalizedStatus,
                      phone: legacyClinicData.phone ?? null,
                      email: legacyClinicData.email ?? null,
                      type: legacyClinicData.type ?? organizationDefaultType ?? null,
                      createdAt: legacyClinicData.createdAt ?? migrationTimestamp,
                      createdBy: legacyClinicData.createdBy ?? currentUser?.uid ?? null,
                      updatedAt: legacyClinicData.updatedAt ?? migrationTimestamp,
                    };
                    updates[`locations/${legacyClinicId}`] = migratedClinicData;
                    updates[`organizationLocations/${organizationId}/${legacyClinicId}`] = {
                      addedAt: legacyClinicData.createdAt ?? migrationTimestamp,
                      status: normalizedStatus,
                      updatedAt: legacyClinicData.updatedAt ?? migrationTimestamp,
                    };
                  }

                  if (Object.keys(updates).length > 0) {
                    await update(ref(database), updates);
                    return;
                  }
                }
              } catch (migrationError) {
                console.warn('Failed to migrate legacy clinics', migrationError);
              }
            })();
          }

          pendingClinicIds = new Set();
          clinicOrder = [];
          clinicDataMap.clear();
          updateClinicsState();
          setClinicsError(null);
          setLoadingClinics(keepLoading);
          return;
        }

        pendingClinicIds = new Set(nextIds);

        for (const clinicId of nextIds) {
          if (!clinicUnsubscribers.has(clinicId)) {
            subscribeToClinic(clinicId);
          } else {
            pendingClinicIds.delete(clinicId);
          }
        }

        if (pendingClinicIds.size > 0) {
          setLoadingClinics(true);
        }

        updateClinicsState();
        if (pendingClinicIds.size === 0) {
          setLoadingClinics(false);
        }
      },
      error => {
        if (!isActive) {
          return;
        }
        console.warn('Failed to subscribe to organization clinics', error);
        cleanupClinicSubscriptions();
        clinicOrder = [];
        pendingClinicIds = new Set();
        setClinics([]);
        setClinicsError('We were unable to load clinics. Please try again later.');
        setLoadingClinics(false);
      },
    );

    return () => {
      isActive = false;
      unsubscribeJoin();
      cleanupClinicSubscriptions();
    };
  }, [profile?.organizationId, profile?.needsOrganizationSetup, profileLoading, currentUser?.uid, organizationDefaultType]);

  useEffect(() => {
    if (clinicModalOpen && clinicNameInputRef.current) {
      clinicNameInputRef.current.focus();
    }
  }, [clinicModalOpen]);

  const cardClass = `${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} rounded-lg p-8 ring-1`;
  const mutedTextClass = `${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`;
  const inputClass = `mt-2 w-full rounded-xl border px-4 py-3 text-base shadow-sm transition focus:outline-none focus:ring-2 ${
    isDarkMode
      ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500'
      : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-600 focus:ring-blue-600'
  }`;
  const primaryActionButtonClass = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition ${isDarkMode ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`;
  const loaderTextClass = isDarkMode ? 'text-slate-200' : 'text-slate-600';
  const emptyStateClass = `${isDarkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-slate-50'} flex flex-col items-center justify-center gap-4 rounded-2xl border px-6 py-12 text-center`;

  const isEditingClinic = Boolean(editingClinicId);
  const isClinicSaveDisabled =
    submittingClinic ||
    !clinicForm.name.trim() ||
    !clinicForm.address.trim() ||
    !clinicForm.city.trim() ||
    !clinicForm.state.trim() ||
    !clinicForm.zip.trim();

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/locations' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/locations' } });
  };

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/locations' },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const resetClinicForm = () => {
    setClinicForm(initialClinicFormState);
    setSubmitError(null);
    setEditingClinicId(null);
  };

  const handleCloseClinicModal = () => {
    if (submittingClinic) {
      return;
    }
    setClinicModalOpen(false);
    resetClinicForm();
  };

  const handleOpenAddClinicModal = () => {
    if (submittingClinic) {
      return;
    }
    setClinicForm(prev => ({
      ...initialClinicFormState,
      type: organizationDefaultType ?? prev.type ?? '',
    }));
    setEditingClinicId(null);
    setSubmitError(null);
    setClinicModalOpen(true);
  };

  const handleOpenEditClinicModal = (clinic: ClinicRecord) => {
    if (submittingClinic) {
      return;
    }
    setClinicForm({
      name: clinic.name ?? '',
      address: clinic.address ?? '',
      city: clinic.city ?? '',
      state: (clinic.state ?? '').toUpperCase().slice(0, 2),
      zip: clinic.zip ?? '',
      description: clinic.description ?? '',
      phone: clinic.phone ?? '',
      email: clinic.email ?? '',
      status: clinic.status ?? 'active',
      type: clinic.type ?? organizationDefaultType ?? '',
    });
    setEditingClinicId(clinic.id);
    setSubmitError(null);
    setClinicModalOpen(true);
  };

  const handleClinicSave = async () => {
    if (!currentUser) {
      setSubmitError('You need to be signed in to manage clinics.');
      return;
    }
    if (!profile?.organizationId) {
      setSubmitError('You need an organization before managing clinics.');
      return;
    }

    const trimmedName = clinicForm.name.trim();
    const trimmedAddress = clinicForm.address.trim();
    const trimmedCity = clinicForm.city.trim();
    const trimmedState = clinicForm.state.trim().toUpperCase();
    const trimmedZip = clinicForm.zip.trim();
    const trimmedDescription = clinicForm.description.trim();
    const trimmedPhone = clinicForm.phone.trim();
    const trimmedEmail = clinicForm.email.trim();
    const trimmedClinicType = clinicForm.type.trim();

    if (!trimmedName) {
      setSubmitError('Clinic name is required.');
      return;
    }
    if (!trimmedAddress) {
      setSubmitError('Clinic address is required.');
      return;
    }
    if (!trimmedCity) {
      setSubmitError('City is required.');
      return;
    }
    if (!trimmedState) {
      setSubmitError('State is required.');
      return;
    }
    if (!trimmedZip) {
      setSubmitError('Zip code is required.');
      return;
    }

    setSubmittingClinic(true);
    setSubmitError(null);

    try {
      const organizationId = profile.organizationId;
      if (!organizationId) {
        throw new Error('Organization id is required to save clinics.');
      }

      const timestamp = Date.now();
      const rootRef = ref(database);

      if (editingClinicId) {
        const clinicId = editingClinicId;
        const existingClinic = clinics.find(clinic => clinic.id === clinicId);
        const baseClinicPath = `locations/${clinicId}`;
        const clinicUpdates: Record<string, unknown> = {
          [`${baseClinicPath}/name`]: trimmedName,
          [`${baseClinicPath}/address`]: trimmedAddress,
          [`${baseClinicPath}/city`]: trimmedCity,
          [`${baseClinicPath}/state`]: trimmedState,
          [`${baseClinicPath}/zip`]: trimmedZip,
          [`${baseClinicPath}/status`]: clinicForm.status,
          [`${baseClinicPath}/phone`]: trimmedPhone || null,
          [`${baseClinicPath}/email`]: trimmedEmail || null,
          [`${baseClinicPath}/type`]:
            trimmedClinicType || existingClinic?.type || organizationDefaultType || null,
          [`${baseClinicPath}/description`]: trimmedDescription || null,
          [`${baseClinicPath}/updatedAt`]: timestamp,
        };

        if (!existingClinic?.organizationId) {
          clinicUpdates[`${baseClinicPath}/organizationId`] = organizationId;
        }
        if (!existingClinic?.createdAt) {
          clinicUpdates[`${baseClinicPath}/createdAt`] = existingClinic?.createdAt ?? timestamp;
        }
        if (!existingClinic?.createdBy) {
          clinicUpdates[`${baseClinicPath}/createdBy`] = existingClinic?.createdBy ?? currentUser.uid;
        }

        await update(rootRef, {
          ...clinicUpdates,
          [`organizationLocations/${organizationId}/${clinicId}/status`]: clinicForm.status,
          [`organizationLocations/${organizationId}/${clinicId}/updatedAt`]: timestamp,
        });
      } else {
        const clinicsRef = ref(database, 'clinics');
        const clinicRef = push(clinicsRef);
        const clinicId = clinicRef.key;
        if (!clinicId) {
          throw new Error('Unable to generate clinic id.');
        }

        const clinicData = {
          id: clinicId,
          organizationId,
          name: trimmedName,
          address: trimmedAddress,
          city: trimmedCity,
          state: trimmedState,
          zip: trimmedZip,
          description: trimmedDescription || null,
          status: clinicForm.status,
          phone: trimmedPhone || null,
          email: trimmedEmail || null,
          createdAt: timestamp,
          createdBy: currentUser.uid,
          updatedAt: timestamp,
          type: trimmedClinicType || organizationDefaultType || null,
        };

        await update(rootRef, {
          [`locations/${clinicId}`]: clinicData,
          [`organizationLocations/${organizationId}/${clinicId}`]: {
            addedAt: timestamp,
            status: clinicForm.status,
            updatedAt: timestamp,
          },
        });
      }

      setClinicModalOpen(false);
      resetClinicForm();
    } catch (error) {
      console.warn('Failed to save clinic', error);
      setSubmitError(
        editingClinicId
          ? 'We were unable to update the clinic. Please try again.'
          : 'We were unable to add the clinic. Please try again.',
      );
    } finally {
      setSubmittingClinic(false);
    }
  };

  const hasOrganizationAccess = Boolean(profile?.organizationId);
  const isContentLoading = profileLoading || (profile?.organizationId ? loadingClinics : false);
  const showEmptyState = !isContentLoading && clinics.length === 0 && !clinicsError;

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Checking clinic access...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  type ClinicCardsListProps = { list: ClinicRecord[] };

  const ClinicCardsList: React.FC<ClinicCardsListProps> = ({ list }) => {
    if (!list.length) return null;

    return (
      <ul className="flex flex-col gap-4">
        {list.map(clinic => (
          <li key={clinic.id}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleOpenEditClinicModal(clinic)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpenEditClinicModal(clinic);
                }
              }}
              className={`${isDarkMode
                ? 'border-slate-800 bg-slate-900/70 hover:border-blue-500/60 hover:bg-slate-900'
                : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/30'
              } relative flex w-full flex-col gap-4 rounded-2xl border p-6 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  {clinic.type ? (
                    <span className={`${isDarkMode ? 'bg-blue-900/60 text-blue-100' : 'bg-blue-100 text-blue-700'} inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold`}>
                      {clinic.type}
                    </span>
                  ) : null}
                  <div>
                    <h2 className="text-xl font-semibold">{clinic.name}</h2>
                    <p className={`${isDarkMode ? 'text-slate-300' : 'text-slate-500'} text-sm`}>
                      {clinicAddressLine(clinic)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    handleOpenEditClinicModal(clinic);
                  }}
                  className={`${isDarkMode ? 'text-blue-300 hover:text-blue-100' : 'text-blue-600 hover:text-blue-700'} inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide`}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                  Edit
                </button>
              </div>

              <img
                src={staticMapUrl(clinic, 640, 400)}
                alt={`Map preview of ${clinic.name}`}
                loading="lazy"
                className={`${isDarkMode ? 'ring-slate-800' : 'ring-slate-200'} w-full rounded-xl ring-1`}
                style={{ aspectRatio: '16 / 10', objectFit: 'cover' }}
              />

              {(clinic.email || clinic.phone) && (
                <div className="flex flex-col gap-3 text-sm">
                  {clinic.email && (
                    <a
                      href={`mailto:${clinic.email}`}
                      onClick={event => event.stopPropagation()}
                      className={`${isDarkMode
                        ? 'border-slate-700 bg-slate-900 text-blue-200 hover:border-blue-500/70'
                        : 'border-slate-200 bg-white text-blue-700 hover:border-blue-200'
                      } inline-flex items-center gap-2 rounded-xl border px-4 py-3 font-semibold transition`}
                    >
                      <Mail className="h-4 w-4" aria-hidden="true" />
                      <span className="truncate">{clinic.email}</span>
                    </a>
                  )}
                  {clinic.phone && (
                    <a
                      href={`tel:${clinic.phone}`}
                      onClick={event => event.stopPropagation()}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                    >
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      <span>{clinic.phone}</span>
                    </a>
                  )}
                </div>
              )}

              {clinic.description ? (
                <div className={`${isDarkMode ? 'bg-slate-800/70 text-slate-100' : 'bg-slate-50 text-slate-700'} rounded-2xl px-4 py-3 text-sm`}>
                  {clinic.description}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      hasOrganizationAccess={hasOrganizationAccess}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
    >
      {isContentLoading ? (
        <div className="flex justify-center pt-12">
          <div className={`flex items-center gap-3 text-sm font-semibold ${loaderTextClass}`}>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading clinics...
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <section className={cardClass}>
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">Manage your locations</h1>

              {clinics.length > 0 && (
                <button
                  type="button"
                  className={primaryActionButtonClass}
                  onClick={handleOpenAddClinicModal}
                  disabled={submittingClinic}
                >
                  <Plus className="h-5 w-5" aria-hidden="true" />
                  Add Location
                </button>
              )}
            </div>

            {/* Subtitle below both */}
            <div className="mt-3 mb-3">
              <p className={mutedTextClass}>
                Track each location in the Happy Tail Tracker app. Add locations as your organization grows.
              </p>
            </div>
            {organizationHasSubscription === false ? (
              <div
                className={`mt-6 space-y-4 rounded-xl border px-4 py-5 text-sm ${
                  isDarkMode
                    ? 'border-amber-300/40 bg-amber-500/10 text-amber-100'
                    : 'border-amber-300 bg-amber-50 text-amber-900'
                }`}
              >
                Upgrade to <strong>Premium</strong> to feature this location when users search for vets or other pet services in the app.{' '}
                <button
                  type="button"
                  onClick={() => navigate('/billing')}
                  className={`font-semibold underline ${isDarkMode ? 'text-amber-100 hover:text-amber-200' : 'text-amber-900 hover:text-amber-700'}`}
                >
                  Upgrade
                </button>
              </div>
            ) : null}

            <div className="mt-6 space-y-6">
              {clinicsError && !loadingClinics && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">
                  {clinicsError}
                </div>
              )}

              {showEmptyState && (
                <div className={emptyStateClass}>
                  <p className="text-lg font-semibold">No clinics found</p>
                  <p className={mutedTextClass}>Add your first clinic to start organizing your locations.</p>
                  <button
                    type="button"
                    className={primaryActionButtonClass}
                    onClick={handleOpenAddClinicModal}
                    disabled={submittingClinic}
                  >
                    <Plus className="h-5 w-5" aria-hidden="true" />
                    Add Clinic
                  </button>
                </div>
              )}

              {!showEmptyState && clinics.length > 0 && (
                <div className="space-y-10">
                  {/* Active */}
                  {groupedClinics.active.length > 0 && (
                    <section>
                      <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Active</h2>
                        <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-500'} text-sm`}>
                          {groupedClinics.active.length} location{groupedClinics.active.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <ClinicCardsList list={groupedClinics.active} />
                    </section>
                  )}

                  {/* Paused */}
                  {groupedClinics.paused.length > 0 && (
                    <section>
                      <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Paused</h2>
                        <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-500'} text-sm`}>
                          {groupedClinics.paused.length} location{groupedClinics.paused.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <ClinicCardsList list={groupedClinics.paused} />
                    </section>
                  )}

                  {/* Closed */}
                  {groupedClinics.closed.length > 0 && (
                    <section>
                      <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Closed</h2>
                        <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-500'} text-sm`}>
                          {groupedClinics.closed.length} location{groupedClinics.closed.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <ClinicCardsList list={groupedClinics.closed} />
                    </section>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
      <ActionModal
        isOpen={clinicModalOpen}
        title={isEditingClinic ? 'Edit clinic' : 'Add clinic'}
        onClose={handleCloseClinicModal}
        onCancel={handleCloseClinicModal}
        onSave={handleClinicSave}
        isDarkMode={isDarkMode}
        isSaving={submittingClinic}
        disableSave={isClinicSaveDisabled}
        saveLabel={isEditingClinic ? 'Save changes' : 'Save clinic'}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="clinic-name" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Clinic name
            </label>
            <input
              id="clinic-name"
              ref={clinicNameInputRef}
              type="text"
              className={inputClass}
              value={clinicForm.name}
              onChange={event => {
                const value = event.target.value;
                setClinicForm(prev => ({ ...prev, name: value }));
              }}
              placeholder="Downtown Veterinary Care"
              disabled={submittingClinic}
            />
          </div>
          <div>
            <label htmlFor="clinic-address" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Clinic address
            </label>
            <input
              id="clinic-address"
              type="text"
              className={inputClass}
              value={clinicForm.address}
              onChange={event => {
                const value = event.target.value;
                setClinicForm(prev => ({ ...prev, address: value }));
              }}
              placeholder="123 Main Street, Suite 200"
              disabled={submittingClinic}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="clinic-city" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                City
              </label>
              <input
                id="clinic-city"
                type="text"
                className={inputClass}
                value={clinicForm.city}
                onChange={event => {
                  const value = event.target.value;
                  setClinicForm(prev => ({ ...prev, city: value }));
                }}
                placeholder="Austin"
                disabled={submittingClinic}
              />
            </div>
            <div>
              <label htmlFor="clinic-state" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                State
              </label>
              <input
                id="clinic-state"
                type="text"
                className={inputClass}
                value={clinicForm.state}
                onChange={event => {
                  const value = event.target.value.toUpperCase().slice(0, 2);
                  setClinicForm(prev => ({ ...prev, state: value }));
                }}
                placeholder="TX"
                maxLength={2}
                disabled={submittingClinic}
              />
            </div>
            <div>
              <label htmlFor="clinic-zip" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Zip
              </label>
              <input
                id="clinic-zip"
                type="text"
                className={inputClass}
                value={clinicForm.zip}
                onChange={event => {
                  const value = event.target.value;
                  setClinicForm(prev => ({ ...prev, zip: value }));
                }}
                placeholder="73301"
                disabled={submittingClinic}
              />
            </div>
          </div>
          <div>
            <label htmlFor="clinic-description" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Description (optional)
            </label>
            <textarea
              id="clinic-description"
              className={`${inputClass} min-h-[140px]`}
              value={clinicForm.description}
              onChange={event => {
                const value = event.target.value;
                setClinicForm(prev => ({ ...prev, description: value }));
              }}
              placeholder="Add notes about services, specialties, parking, or hours."
              disabled={submittingClinic}
              rows={5}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="clinic-status" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Status
              </label>
              <select
                id="clinic-status"
                className={inputClass}
                value={clinicForm.status}
                onChange={event => {
                  const value = event.target.value as ClinicRecord['status'];
                  setClinicForm(prev => ({ ...prev, status: value }));
                }}
                disabled={submittingClinic}
              >
                {clinicStatusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="clinic-type" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Location type
              </label>
              <select
                id="clinic-type"
                className={inputClass}
                value={clinicForm.type}
                onChange={event => {
                  const value = event.target.value;
                  setClinicForm(prev => ({ ...prev, type: value }));
                }}
                disabled={submittingClinic}
              >
                <option value="">Use organization default</option>
                {locationTypeOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="clinic-phone" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Phone (optional)
              </label>
              <input
                id="clinic-phone"
                type="tel"
                className={inputClass}
                value={clinicForm.phone}
                onChange={event => {
                  const value = event.target.value;
                  setClinicForm(prev => ({ ...prev, phone: value }));
                }}
                placeholder="(555) 123-4567"
                disabled={submittingClinic}
              />
            </div>
            <div>
              <label htmlFor="clinic-email" className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Email (optional)
              </label>
              <input
                id="clinic-email"
                type="email"
                className={inputClass}
                value={clinicForm.email}
                onChange={event => {
                  const value = event.target.value;
                  setClinicForm(prev => ({ ...prev, email: value }));
                }}
                placeholder="contact@clinic.com"
                disabled={submittingClinic}
              />
            </div>
          </div>
          {submitError && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">
              {submitError}
            </div>
          )}
        </div>
      </ActionModal>
    </DashboardLayout>
  );
}
